import logger from '../log';
import BaseInteraction from './BaseInteraction';
import { getVideoJsVideoElement } from '../common/utils/videoElementUtils';
import bindVideoJsToWebRTC from '../webrtc/bindVideoJsToWebRTC';
import { SourceType } from '../common/constants';
import ContentItem from '../api/content/ContentItem';
import {
  getMimeForSourceType,
  getSourceTypeForMime
} from '../common/utils/sourceTypeUtils';
import { VideoJSPlayerExtended } from './interfaces';
import {
  ItemSource,
  PlayerNames,
  VideoJSOptions,
  VideoJSPlayer
} from '@kollective-frontend/ksdk-api';
import { Types, checkType, logUnableToSetupMessage } from '../util/checkTypes';
import { SourceSetEventOptions } from '../events';
import { TechApi } from '../bootloader/tech';
const log = logger.getLogger(__filename);

export interface ResetController {
  controlled: () => boolean;
}

export const getMimeType = (item: ContentItem): string | undefined => {
  if (item && item.data && item.data.mime) {
    return item.data.mime;
  }
  const sourceType = getSourceTypeForMime(undefined, item.url);
  return (sourceType && getMimeForSourceType(sourceType)) || undefined;
};

export default class VideoJsInteraction extends BaseInteraction<VideoJSPlayer> {
  playerOptions: VideoJSOptions;
  resetController: ResetController;
  hasReset = false;

  constructor(
    player: VideoJSPlayer,
    tech: TechApi,
    playerOptions?: VideoJSOptions
  ) {
    super(player, tech);
    checkType(player, Types.VIDEOJS).emitErrorIfNotCorrectType();
    this.playerOptions = playerOptions ?? {};
    this.resetController = { controlled: () => this.hasReset };
  }

  initialize(): void {
    super.initialize();
    this.bindVideoJs();
  }

  bindVideoJs() {
    const result = checkType(this.player, Types.VIDEOJS);
    if (result.isType) {
      bindVideoJsToWebRTC(
        this.player,
        this.playerOptions,
        this.binder,
        this.tech,
        method => this.cleanupMethods.push(method)
      );
    } else {
      logUnableToSetupMessage(PlayerNames.VIDEOJS_PLAYER, log, result);
    }
  }

  resetEventsAndInterceptors(opts: SourceSetEventOptions<VideoJSPlayer>): void {
    this.resetMediaElement(opts.options?.player);
    this.bindVideoJs();
  }

  loader(items: ContentItem[]): ContentItem[] {
    const src = items.map(i => {
      return { src: i.url, type: getMimeType(i) };
    });
    this.setSource(src);
    return items;
  }

  setSource(src: ItemSource[]): void {
    const result = checkType(this.player, Types.VIDEOJS);
    if (result.isType) {
      this.player.src(src);
    } else {
      result.log(log, log.ERROR, 'Unable to set source');
    }
  }

  clearSource(): void {
    log.warn(this.generatePlannedErrorMessage('clearSource', 'VideoJs'));
    // Implementing this should be as simple as uncommenting this and testing it.
    // this.setSource(null);
  }

  mediaElement(): HTMLMediaElement | undefined {
    if (typeof (this.player as VideoJSPlayerExtended)?.el !== 'function') {
      log.info('mediaElement: player is not configured correctly', this.player);
      return undefined;
    }
    return getVideoJsVideoElement(this.player);
  }

  getAllowedSourceTypes(): SourceType[] {
    return [SourceType.DASH, SourceType.HLS];
  }
}
